import {
  type AnsweredValue,
  asMemberId,
  type FindingComponent,
  type FindingComponentAnswer,
  type ParameterValue,
  type QuantityDeclaration,
  quantityText,
} from "@repo/domain";

/**
 * Answered values to and from form fields, and to display text, keyed on the
 * value declaration so a trial parameter and a measured finding component read
 * and write the same way. Option identity is the domain's `id`; callers holding
 * a presented `optionId` normalize before calling.
 */

const booleanLabel = (value: boolean) => (value ? "Yes" : "No");

/** A yes/no field's options. The field value is `String(boolean)`. */
export const BOOLEAN_FIELD_OPTIONS = [true, false].map((value) => ({
  value: String(value),
  label: booleanLabel(value),
}));

/** What reading a field needs from a declaration: its kind, and a quantity's unit. */
export type AnsweringDeclaration =
  | Pick<QuantityDeclaration, "kind" | "unit">
  | { kind: Exclude<ParameterValue["kind"], "Quantity"> };

/** What display needs from a declaration: a quantity's presentation hint and choice labels by option id. */
export type DisplayedDeclaration =
  | Pick<QuantityDeclaration, "kind" | "display">
  | { kind: "Choice"; options: readonly { id?: string; label: string }[] }
  | { kind: Exclude<ParameterValue["kind"], "Quantity" | "Choice"> };

/** The answer a form field holds, or undefined when it holds none. Numbers may arrive typed or as text. */
export function answerFromField(
  declaration: AnsweringDeclaration,
  raw: unknown,
  freeText: unknown = "",
): AnsweredValue | undefined {
  switch (declaration.kind) {
    case "Choice": {
      const selected = (Array.isArray(raw) ? raw.map(String) : typeof raw === "string" && raw ? [raw] : []).map(
        asMemberId,
      );
      const text = typeof freeText === "string" ? freeText.trim() : "";
      if (!selected.length && !text) return undefined;
      return { kind: "Choice", selected, ...(text ? { freeText: text } : {}) };
    }
    case "Boolean":
      return raw === "true" || raw === "false" ? { kind: "Boolean", value: raw === "true" } : undefined;
    case "Text":
      return typeof raw === "string" && raw.trim() ? { kind: "Text", value: raw } : undefined;
    default: {
      const value = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : undefined;
      if (value === undefined) return undefined;
      return declaration.kind === "Quantity"
        ? { kind: "Quantity", value, ...(declaration.unit ? { unit: declaration.unit } : {}) }
        : { kind: "Integer", value };
    }
  }
}

/** The form field value for an answer: a list for a multiple choice, text otherwise. */
export function fieldFromAnswer(declaration: ParameterValue, answer?: AnsweredValue): string | string[] {
  const multiple = declaration.kind === "Choice" && declaration.cardinality === "Multiple";
  if (!answer) return multiple ? [] : "";
  if (answer.kind === "Choice") return multiple ? [...answer.selected] : (answer.selected[0] ?? "");
  return String(answer.value);
}

/** An answer as one line of text. Choice options fall back to their id when the declaration does not name them. */
export function displayAnsweredValue(
  declaration: DisplayedDeclaration | undefined,
  value:
    | Exclude<AnsweredValue, { kind: "Choice" }>
    | { kind: "Choice"; selected: readonly string[]; freeText?: string },
): string {
  switch (value.kind) {
    case "Choice": {
      const options = declaration?.kind === "Choice" ? declaration.options : [];
      return [
        ...value.selected.map((id) => options.find((option) => option.id === id)?.label ?? id),
        ...(value.freeText ? [value.freeText] : []),
      ].join(", ");
    }
    case "Boolean":
      return booleanLabel(value.value);
    case "Quantity": {
      const text = quantityText(declaration, {
        ...(value.unit ? { unit: { display: value.unit.display || value.unit.code } } : {}),
      });
      return `${text.format(value.value)}${text.suffix === undefined ? "" : ` ${text.suffix}`}`;
    }
    default:
      return String(value.value);
  }
}

/** A measured finding's values as one line, each labelled by its component when there is more than one. */
export function displayMeasuredValues(
  components: readonly FindingComponent[],
  values: readonly FindingComponentAnswer[],
): string {
  return components
    .flatMap((component) => {
      const answer = values.find((candidate) => candidate.componentId === component.id);
      if (!answer) return [];
      const text = displayAnsweredValue(component.value, answer);
      return [components.length > 1 ? `${component.label}: ${text}` : text];
    })
    .join("; ");
}
